import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { localizedValue } from '../localization/presentationLanguage';
const feedText = (es, en) => localizedValue({ es, en });

function getSafeImageUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = raw
    .replace(/^http:\/\/localhost:5001/i, 'https://api.zodiacus.app')
    .replace(/^http:\/\/127\.0\.0\.1:5001/i, 'https://api.zodiacus.app');

  if (/^https?:\/\//i.test(normalized)) return normalized;
  if (/^blob:/i.test(normalized)) return normalized;
  if (/^data:image\//i.test(normalized)) return normalized;
  if (normalized.startsWith('/')) return normalized;
  return '';
}

function clampScale(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.min(2.6, Math.max(1, Number(numeric.toFixed(2))));
}

export default function FeedImageViewer({
  open = false,
  image = null,
  isMobile = false,
  onClose,
}) {
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [scale, setScale] = useState(1);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const frameRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchCurrentRef = useRef(null);
  const lastTapRef = useRef(0);
    const panStartRef = useRef(null);
    const pointerPanActiveRef = useRef(false);
const pointerPanIdRef = useRef(null);

  const imageUrl = useMemo(() => {
    return getSafeImageUrl(image?.url || image?.thumbUrl || '');
  }, [image]);

  const altText = useMemo(() => {
    const explicitAlt = String(image?.alt || '').trim();
    if (explicitAlt) return explicitAlt;
    return 'Imagen ampliada del post';
  }, [image]);

  const canZoomIn = scale < 2.6;
  const canZoomOut = scale > 1;
  const canDragToClose = isMobile && scale <= 1.02;

  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => clampScale(prev + 0.2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => clampScale(prev - 0.2));
  }, []);

  const handleResetZoom = useCallback(() => {
    setScale(1);
    setDragY(0);
    setPanX(0);
    setPanY(0);
    setDragging(false);
    touchStartRef.current = null;
      touchCurrentRef.current = null;
      panStartRef.current = null;
pointerPanActiveRef.current = false;
pointerPanIdRef.current = null;
  }, []);

  const handleDoubleTapZoom = useCallback(() => {
    setScale((prev) => {
      const nextScale = prev > 1.2 ? 1 : 2;
      if (nextScale <= 1.02) {
        setPanX(0);
        setPanY(0);
      }
      return nextScale;
    });
    setDragY(0);
    setPanX(0);
    setPanY(0);
    setDragging(false);
    touchStartRef.current = null;
    touchCurrentRef.current = null;
    panStartRef.current = null;
  }, []);

  const handleTouchStart = useCallback((event) => {
    const touch = event?.touches?.[0];
    if (!touch) return;

    if (scale > 1.02) {
      panStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        panX,
        panY,
      };
      setDragging(true);
      return;
    }

    if (!canDragToClose) return;

    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      ts: Date.now(),
    };
    touchCurrentRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
    setDragging(true);
  }, [canDragToClose, panX, panY, scale]);

  const handleTouchMove = useCallback((event) => {
    const touch = event?.touches?.[0];
    if (!touch) return;

    if (scale > 1.02 && panStartRef.current) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      const nextPanX = panStartRef.current.panX + (touch.clientX - panStartRef.current.x);
      const nextPanY = panStartRef.current.panY + (touch.clientY - panStartRef.current.y);
      setPanX(nextPanX);
      setPanY(nextPanY);
      return;
    }

    if (!canDragToClose || !touchStartRef.current) return;

    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaX = touch.clientX - touchStartRef.current.x;

    if (Math.abs(deltaY) < Math.abs(deltaX)) return;

    touchCurrentRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };

    if (deltaY > 0) {
      setDragY(deltaY);
    } else {
      setDragY(deltaY * 0.18);
    }
  }, [canDragToClose, scale]);

  const handleTouchEnd = useCallback(() => {
    if (scale > 1.02) {
      panStartRef.current = null;
      setDragging(false);
      return;
    }

    const start = touchStartRef.current;
    const current = touchCurrentRef.current;
    const now = Date.now();

    if (start && current) {
      const deltaY = current.y - start.y;
      const deltaX = current.x - start.x;
      const deltaTime = now - start.ts;
      const absY = Math.abs(deltaY);
      const absX = Math.abs(deltaX);

      const wasTap = absY < 10 && absX < 10 && deltaTime < 250;
      if (wasTap) {
        if (now - lastTapRef.current < 280) {
          lastTapRef.current = 0;
          handleDoubleTapZoom();
        } else {
          lastTapRef.current = now;
        }
      } else if (deltaY > 120 && absY > absX) {
        handleClose();
      }
    }

    touchStartRef.current = null;
    touchCurrentRef.current = null;
    panStartRef.current = null;
    setDragging(false);
    setDragY(0);
  }, [handleClose, handleDoubleTapZoom, scale]);
    
    const handlePointerDown = useCallback((event) => {
  if (scale <= 1.02) return;
  if (event?.pointerType === 'touch') return;

  pointerPanActiveRef.current = true;
  pointerPanIdRef.current = event.pointerId;
  panStartRef.current = {
    x: event.clientX,
    y: event.clientY,
    panX,
    panY,
  };
  setDragging(true);

  if (event.currentTarget?.setPointerCapture) {
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {}
  }
}, [panX, panY, scale]);

const handlePointerMove = useCallback((event) => {
  if (!pointerPanActiveRef.current || scale <= 1.02 || !panStartRef.current) return;

  const nextPanX = panStartRef.current.panX + (event.clientX - panStartRef.current.x);
  const nextPanY = panStartRef.current.panY + (event.clientY - panStartRef.current.y);
  setPanX(nextPanX);
  setPanY(nextPanY);
}, [scale]);

const handlePointerEnd = useCallback((event) => {
  if (!pointerPanActiveRef.current) return;

  pointerPanActiveRef.current = false;
  pointerPanIdRef.current = null;
  panStartRef.current = null;
  setDragging(false);

  if (event?.currentTarget?.releasePointerCapture && event?.pointerId != null) {
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {}
  }
}, []);
    
    const handleWheel = useCallback((event) => {
  if (scale <= 1.02) return;
  if (!event) return;

  event.preventDefault();

  const deltaX = Number(event.deltaX || 0);
  const deltaY = Number(event.deltaY || 0);

  setPanX((prev) => prev - deltaX);
  setPanY((prev) => prev - deltaY);
}, [scale]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (!open) {
      setVisible(false);
      setLoaded(false);
      setFailed(false);
      setScale(1);
setDragY(0);
setPanX(0);
setPanY(0);
setDragging(false);
touchStartRef.current = null;
touchCurrentRef.current = null;
panStartRef.current = null;
pointerPanActiveRef.current = false;
pointerPanIdRef.current = null;
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      return undefined;
    }

    frameRef.current = window.requestAnimationFrame(() => {
      setVisible(true);
      frameRef.current = null;
    });

    return () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    setScale(1);
    setDragY(0);
    setPanX(0);
    setPanY(0);
    setDragging(false);
    touchStartRef.current = null;
    touchCurrentRef.current = null;
    panStartRef.current = null;
  }, [imageUrl]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }

      if (failed) return;

      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        handleZoomIn();
        return;
      }

      if (event.key === '-') {
        event.preventDefault();
        handleZoomOut();
        return;
      }

      if (event.key === '0') {
        event.preventDefault();
        handleResetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, failed, handleClose, handleResetZoom, handleZoomIn, handleZoomOut]);

  useEffect(() => {
    if (scale <= 1.02) {
      setPanX(0);
      setPanY(0);
      panStartRef.current = null;
    }
  }, [scale]);

  if (!open || !imageUrl) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Vista ampliada de la imagen del post"
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain',
        touchAction: 'pan-y pinch-zoom',
        padding: isMobile ? '14px 14px 120px' : '26px',
        background: visible ? `rgba(7, 5, 14, ${Math.max(0.44, 0.88 - Math.min(0.34, dragY / 700))})` : 'rgba(7, 5, 14, 0)',
        backdropFilter: visible ? 'blur(18px)' : 'blur(0px)',
        WebkitBackdropFilter: visible ? 'blur(18px)' : 'blur(0px)',
        transition: 'background 220ms ease, backdrop-filter 220ms ease',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(480px 220px at 50% 0%, rgba(255,224,130,0.12), transparent 60%), radial-gradient(340px 180px at 100% 100%, rgba(168,120,255,0.10), transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleClose();
        }}
        aria-label={feedText('Cerrar imagen', 'Close image')}
        style={{
          position: 'absolute',
          top: isMobile ? 14 : 18,
          right: isMobile ? 14 : 18,
          width: isMobile ? 44 : 48,
          height: isMobile ? 44 : 48,
          borderRadius: 999,
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(15, 10, 24, 0.72)',
          color: '#FFF5DC',
          fontSize: isMobile ? 18 : 20,
          fontWeight: 900,
          cursor: 'pointer',
          zIndex: 10002,
          boxShadow: '0 12px 28px rgba(0,0,0,0.26), 0 0 18px rgba(255,224,130,0.06)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        ×
      </button>

      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: 'absolute',
          left: isMobile ? 14 : 18,
          top: isMobile ? 14 : 18,
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          maxWidth: isMobile ? 'calc(100% - 72px)' : 'calc(100% - 110px)',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: isMobile ? '8px 10px' : '9px 12px',
            borderRadius: 999,
            background: 'rgba(15, 10, 24, 0.68)',
            border: '1px solid rgba(255,255,255,0.10)',
            color: '#FFF5DC',
            fontSize: isMobile ? 11 : 11.5,
            fontWeight: 800,
            letterSpacing: 0.16,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <span aria-hidden="true">✦</span>
          Vista premium
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: isMobile ? '8px 10px' : '9px 12px',
            borderRadius: 999,
            background: 'rgba(15, 10, 24, 0.68)',
            border: '1px solid rgba(255,255,255,0.10)',
            color: 'rgba(255,245,220,0.88)',
            fontSize: isMobile ? 11 : 11.5,
            fontWeight: 800,
            letterSpacing: 0.16,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          Zoom {Math.round(scale * 100)}%
        </div>
      </div>

      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: 'absolute',
          right: isMobile ? 14 : 18,
          bottom: isMobile ? 'max(18px, calc(env(safe-area-inset-bottom, 0px) + 18px))' : 22,
          zIndex: 10002,
          pointerEvents: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: isMobile ? '8px 10px' : '9px 12px',
          borderRadius: 999,
          background: 'rgba(15, 10, 24, 0.68)',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 10px 22px rgba(0,0,0,0.18)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <button
          type="button"
          disabled={!canZoomOut}
          onClick={handleZoomOut}
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.10)',
            background: canZoomOut ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.03)',
            color: canZoomOut ? '#FFF5DC' : 'rgba(255,245,220,0.40)',
            fontSize: 18,
            fontWeight: 900,
            cursor: canZoomOut ? 'pointer' : 'default',
          }}
        >
          −
        </button>

        <button
          type="button"
          onClick={handleResetZoom}
          style={{
            minWidth: 64,
            height: 34,
            padding: '0 12px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.07)',
            color: '#FFF5DC',
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: 0.16,
            cursor: 'pointer',
          }}
        >
          Reset
        </button>

        <button
          type="button"
          disabled={!canZoomIn}
          onClick={handleZoomIn}
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.10)',
            background: canZoomIn ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.03)',
            color: canZoomIn ? '#FFF5DC' : 'rgba(255,245,220,0.40)',
            fontSize: 18,
            fontWeight: 900,
            cursor: canZoomIn ? 'pointer' : 'default',
          }}
        >
          +
        </button>
      </div>

      <div
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
              onPointerDown={handlePointerDown}
                onWheel={handleWheel}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        style={{
          position: 'relative',
          maxWidth: 'min(100%, 1180px)',
          maxHeight: 'none',
          minHeight: isMobile ? 'calc(100vh - 140px)' : 'auto',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflowX: 'hidden',
          paddingBottom: isMobile ? 'max(98px, calc(env(safe-area-inset-bottom, 0px) + 98px))' : 110,
          overflow: 'hidden',
          touchAction: scale > 1 ? 'none' : 'pan-y pinch-zoom',
          cursor: scale > 1.02 ? (dragging ? 'grabbing' : 'grab') : 'default',
          transform: scale > 1.02
            ? `translate(${Math.round(panX)}px, ${Math.round(panY)}px)`
            : `translate(0px, ${Math.round(dragY)}px)`,
          transition: dragging ? 'none' : 'transform 220ms ease',
        }}
      >
        {!loaded && !failed ? (
          <div
            style={{
              position: 'absolute',
              inset: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 16px',
              borderRadius: 999,
              background: 'rgba(15, 10, 24, 0.70)',
              border: '1px solid rgba(255,255,255,0.10)',
              color: '#FFF5DC',
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 0.18,
              boxShadow: '0 12px 28px rgba(0,0,0,0.24)',
            }}
          >
            {feedText('Cargando imagen...', 'Loading image...')}
          </div>
        ) : null}

        {!failed ? (
          <img
            src={imageUrl}
            alt={altText}
            loading="eager"
            decoding="async"
            draggable={false}
            referrerPolicy="no-referrer"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: isMobile ? '82vh' : '88vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: isMobile ? 18 : 24,
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow:
                '0 24px 56px rgba(0,0,0,0.34), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 34px rgba(255,224,130,0.08)',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              touchAction: 'none',
              background: 'rgba(255,255,255,0.03)',
              transform: visible
  ? scale > 1.02
    ? `scale(${scale})`
    : `scale(${Math.max(0.92, scale - Math.min(0.12, dragY / 1600))})`
  : 'scale(0.96)',
              transformOrigin: 'center center',
              opacity: loaded ? 1 : 0,
              transition: dragging ? 'opacity 220ms ease' : 'transform 220ms ease, opacity 220ms ease',
              willChange: 'transform, opacity',
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isMobile ? '50vh' : '56vh',
              width: 'min(100%, 760px)',
              padding: '24px 18px',
              textAlign: 'center',
              borderRadius: isMobile ? 18 : 24,
              border: '1px solid rgba(255,120,120,0.14)',
              background: 'rgba(255,255,255,0.03)',
              color: 'rgba(255,245,220,0.82)',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 800,
              letterSpacing: 0.16,
              boxShadow: '0 18px 38px rgba(0,0,0,0.24)',
            }}
          >
            No se pudo cargar la imagen ampliada
          </div>
        )}
      </div>

      {isMobile ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: isMobile ? 66 : 74,
            transform: 'translateX(-50%)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 999,
            background: 'rgba(15, 10, 24, 0.58)',
            border: '1px solid rgba(255,255,255,0.10)',
            color: 'rgba(255,245,220,0.82)',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.14,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            pointerEvents: 'none',
          }}
        >
          {scale > 1.02 ? feedText('Arrastrá para recorrer la imagen · Reset para volver', 'Drag to explore the image · Reset to return') : feedText('Deslizá hacia abajo para cerrar · Doble toque para zoom', 'Swipe down to close · Double-tap to zoom')}
        </div>
      ) : null}
    </div>
  );
}
